import { PlanogramBay } from "@CommonTypes/merchflow/pog/bay";
import { PlanogramItem, PlanogramItemOrientation } from "@CommonTypes/merchflow/pog/item";
import { Planogram } from "@CommonTypes/merchflow/pog/planogram";
import { Pog, PogNonCircular } from "@CommonTypes/merchflow/pog/pog";
import { PlanogramShelf } from "@CommonTypes/merchflow/pog/shelf";
import { uuid } from "@CommonUtils/uuid";
import { FixtureCanCombine } from "../previewSectioning";
import { getPsaSqueezeFlag, getPsaSqueezeSizeMode } from "./PsaSqueeze";
import { getProductIdentity } from "./placement/productIdentity";

const escapePsaField = (value: unknown): string => {
  if (typeof value === "string") {
    return value.replace(/,/g, "\\,");
  }
  return String(value ?? "");
};

const joinPsaRecord = (record: Record<string, unknown>) =>
  Object.values(record).map(escapePsaField).join(",");

const getMerchandisingStyleCode = (merchandisingStyle: PlanogramItem["merchandisingStyle"]) => {
  switch (merchandisingStyle) {
    case "TRAY":
      return 1;
    case "CASE":
      return 2;
    case "UNIT":
      return 0;
  }
};

const getOrientationCode = (orientation: PlanogramItemOrientation) => {
  switch (orientation) {
    case "FRONT2":
      return 1;
    case "SIDE":
      return 2;
    case "SIDE2":
      return 3;
    case "TOP":
      return 4;
    case "TOP2":
      return 5;
    case "FRONT":
      return 0;
  }
};

class PsaAdapter {
  public convertToPsa = (
    _pog: PogNonCircular | Pog,
    options: { planogramName?: string } = {},
  ) => {
    const pog = "getItem" in _pog ? _pog : new Pog(_pog);

    // Space Planning Plus loads 2021-width rows as a blank plan; the donor
    // files that open cleanly are 2019.1.1 with CRLF line endings throughout.
    const psaHeaderString = [
      "PROSPACE SCHEMATIC FILE",
      "; Version 2019.1.1",
      "; Codepage=UTF-8",
    ].join("\r\n");
    const psaProject = this.convertPogToPsaProject();
    const psaProducts: Array<ReturnType<typeof this.convertItemToPsaProduct>> = [];
    const psaPlanogram = this.convertPlanogramToPsaPlanogram(pog.planogram, options.planogramName);
    const psaSegments: Array<ReturnType<typeof this.convertPlanogramBayToPsaSegment>> = [];
    const psaFixturesPositions: Array<
      | ReturnType<typeof this.convertPlanogramShelfToPsaFixture>
      | ReturnType<typeof this.convertPlanogramItemToPsaPosition>
    > = [];

    // UPC is the product's primary key in Space Planning: one Product row
    // per UPC, first-seen id wins (real data carries id variants like
    // "050481" vs "50481" for one UPC), and every Position row references
    // the selected id so placements stay resolvable. Items without a UPC
    // fall back to their own id as the identity.
    const productIdByProductKey = new Map<string, PlanogramItem["productCode"]>();

    for (const bay of pog.planogram.bays) {
      psaSegments.push(this.convertPlanogramBayToPsaSegment(bay));

      for (const shelf of bay.shelves) {
        psaFixturesPositions.push(this.convertPlanogramShelfToPsaFixture(shelf));

        for (const item of shelf.items) {
          const productKey = getProductIdentity(item);
          if (!productIdByProductKey.has(productKey)) {
            productIdByProductKey.set(productKey, item.productCode);
            psaProducts.push(this.convertItemToPsaProduct(item));
          }
          psaFixturesPositions.push(
            this.convertPlanogramItemToPsaPosition(item, productIdByProductKey.get(productKey)),
          );
        }
      }
    }

    return {
      // Record order matters: donor files list Project → Planogram → Segment
      // → Product → Fixture/Position; Product rows before their Planogram row
      // load as a blank plan. The UTF-8 BOM belongs to the string itself:
      // every consumer (browser download, backend blob upload) writes it
      // verbatim, and some Space Planning builds render BOM-less files blank.
      psaString: [
        "\uFEFF" + psaHeaderString,
        joinPsaRecord(psaProject),
        joinPsaRecord(psaPlanogram),
        ...psaSegments.map((psaSegment) => joinPsaRecord(psaSegment)),
        ...psaProducts.map((psaProduct) => joinPsaRecord(psaProduct)),
        ...psaFixturesPositions.map((psaFixturePosition) => joinPsaRecord(psaFixturePosition)),
      ].join("\r\n"),
      psaProject,
      psaProducts,
      psaPlanogram,
      psaSegments,
      psaFixturesPositions,
    };
  };

  private convertPogToPsaProject = () => {
    const psaProject = {
      object: "Project",
      name: "EXPORT",
      key: "",
      primaryKey: 0,
      layoutFile: "",
      movementPeriod: 7,
      caseMultiple: 1.5,
      daysSupply: 1.5,
      demandCycleLength: 1,
      peakSafetyFactor: 1,
      backroomStock: 0,
      pegId: "",
      measurement: 1,
      numberOfStores: 1,

      merchXMin: 0.0,
      merchXMax: 0.0,
      merchXUprights: 0,
      merchXCaps: 0,
      merchXPlacementLeftRight: 3,
      merchXNumberOfFacingsWide: 1,
      merchXWidthOfFacings: 1,
      merchXDirection: 0,
      merchXSqueeze: 1,

      merchYMin: 0,
      merchYMax: 0,
      merchYUprights: 0,
      merchYCaps: 0,
      merchYPlacementUpDown: 2,
      merchYNumberOfUnitsHigh: 3,
      merchYHeightOfUnits: 1,
      merchYDirection: 0,
      merchYSqueeze: 1,

      merchZMin: 0,
      merchZMax: 0,
      merchZUprights: 0,
      merchZCaps: 0,
      merchZPlacementBackFront: 3,
      merchZNumberOfUnitsDeep: 3,
      merchZDepthOfUnits: 1,
      merchZDirection: 1,
      merchZSqueeze: 1,
      demand1: 0,
      demand2: 0,
      demand3: 0,
      demand4: 0,
      demand5: 0,
      demand6: 0,
      demand7: 0,
      demand8: 0,
      demand9: 0,
      demand10: 0,
      demand11: 0,
      demand12: 0,
      demand13: 0,
      demand14: 0,
      demand15: 0,
      demand16: 0,
      demand17: 0,
      demand18: 0,
      demand19: 0,
      demand20: 0,
      demand21: 0,
      demand22: 0,
      demand23: 0,
      demand24: 0,
      demand25: 0,
      demand26: 0,
      demand27: 0,
      demand28: 0,
      inventoryModelManual: 0,
      inventoryModelCaseMultiple: 0,
      inventoryModelDaysSupply: 0,
      inventoryModelPeak: 0,
      inventoryModelMinUnits: 0,
      inventoryModelMaxUnits: 0,
      value1: 0.0,
      value2: 0.0,
      value3: 0.0,
      value4: 0.0,
      value5: 0.0,
      value6: 0.0,
      value7: 0.0,
      value8: 0.0,
      value9: 0.0,
      value10: 0.0,
      value11: 0.0,
      value12: 0.0,
      value13: 0.0,
      value14: 0.0,
      value15: 0.0,
      value16: 0.0,
      value17: 0.0,
      value18: 0.0,
      value19: 0.0,
      value20: 0.0,
      value21: 0.0,
      value22: 0.0,
      value23: 0.0,
      value24: 0.0,
      value25: 0.0,
      value26: 0.0,
      value27: 0.0,
      value28: 0.0,
      value29: 0.0,
      value30: 0.0,
      value31: 0.0,
      value32: 0.0,
      value33: 0.0,
      value34: 0.0,
      value35: 0.0,
      value36: 0.0,
      value37: 0.0,
      value38: 0.0,
      value39: 0.0,
      value40: 0.0,
      value41: 0.0,
      value42: 0.0,
      value43: 0.0,
      value44: 0.0,
      value45: 0.0,
      value46: 0.0,
      value47: 0.0,
      value48: 0.0,
      value49: 0.0,
      value50: 0.0,
      desc1: "",
      desc2: "",
      desc3: "",
      desc4: "",
      desc5: "",
      desc6: "",
      desc7: "",
      desc8: "",
      desc9: "",
      desc10: "",
      desc11: "",
      desc12: "",
      desc13: "",
      desc14: "",
      desc15: "",
      desc16: "",
      desc17: "",
      desc18: "",
      desc19: "",
      desc20: "",
      desc21: "",
      desc22: "",
      desc23: "",
      desc24: "",
      desc25: "",
      desc26: "",
      desc27: "",
      desc28: "",
      desc29: "",
      desc30: "",
      desc31: "",
      desc32: "",
      desc33: "",
      desc34: "",
      desc35: "",
      desc36: "",
      desc37: "",
      desc38: "",
      desc39: "",
      desc40: "",
      desc41: "",
      desc42: "",
      desc43: "",
      desc44: "",
      desc45: "",
      desc46: "",
      desc47: "",
      desc48: "",
      desc49: "",
      desc50: "",
      flag1: 0,
      flag2: 0,
      flag3: 0,
      flag4: 0,
      flag5: 0,
      flag6: 0,
      flag7: 0,
      flag8: 0,
      flag9: 0,
      flag10: 0,
      notes: "",
      changed: 0,
      dbKey1: -1,
      dbKey2: -1,
      dbKey3: -1,
      dbKey4: -1,
      dbKey5: -1,
      dbKey6: -1,
      dbKey7: -1,
      dbKey8: -1,
      dbKey9: -1,
      dbKey10: -1,
      usePerformancePrice: 2,
      usePerformanceCost: 2,
      usePerformanceTaxCode: 2,
      usePerformanceMovement: 2,
      status: "",
      dateCreated: 0,
      dateModified: 0,
      datePending: 0,
      dateEffective: 0,
      dateFinished: 0,
      date1: 0,
      date2: 0,
      date3: 0,
      createdBy: "",
      modifiedBy: "",
      planogramSpecificInventory: 1,
      deliverySchedule: "",
      dbFamilyKey: -1,
      dbReplaceKey: -1,
    };

    return psaProject;
  };

  private convertItemToPsaProduct = (item: PlanogramItem) => {
    const psaProduct = {
      object: "Product",
      upc: item.upc,
      id: item.productCode,
      name: item.name,
      key: 0,
      width: item.unitWidth / 100,
      height: item.unitHeight / 100,
      depth: item.unitDepth / 100,
      color: Number(item.color),
      abbrevName: "",
      size: item.size,
      uom: "S",
      manufacturer: "",
      category: item.categoryCode,
      supplier: "",
      innerPack: 0,
      xNesting: 0,
      yNesting: 0,
      zNesting: 0,
      pegholes: 0,
      pegholeX: 0,
      pegholeY: 0,
      pegholeWidth: 0,
      peghole2X: 0,
      peghole2Y: 0,
      peghole2Width: 0,
      peghole3X: 0,
      peghole3Y: 0,
      peghole3Width: 0,
      packageStyle: 0,
      pegId: "",
      fingerSpaceY: 0,
      jumbleFactor: 0,
      price: item.price,
      caseCost: 0,
      taxCode: 0,
      unitMovement: 0,
      share: 0,
      caseMultiple: 0,
      daysSupply: 0,
      combinedPerformanceIndex: 0,
      pegSpan: 0,
      minimumUnits: 0,
      maximumUnits: 0,
      shapeId: "",
      bitmapIdOverride: "",
      trayWidth: item.trayWidth / 100,
      trayHeight: item.trayHeight / 100,
      trayDepth: item.trayDepth / 100,
      trayNumberWide: item.unitsTrayWide,
      trayNumberHigh: item.unitsTrayHigh,
      trayNumberDeep: item.unitsTrayDeep,
      trayTotalNumber: item.unitsTrayWide * item.unitsTrayHigh * item.unitsTrayDeep,
      trayMaxHigh: 0,
      caseWidth: item.caseWidth / 100,
      caseHeight: item.caseHeight / 100,
      caseDepth: item.caseDepth / 100,
      caseNumberWide: item.unitsCaseWide,
      caseNumberHigh: item.unitsCaseHigh,
      caseNumberDeep: item.unitsCaseDeep,
      caseTotalNumber: item.noOfUnitsInCase,
      caseMaxHigh: 0,
      displayWidth: item.displayWidth,
      displayHeight: item.displayHeight,
      displayDepth: item.displayDepth,
      displayNumberWide: 0,
      displayNumberHigh: 0,
      displayNumberDeep: 0,
      displayTotalNumber: 0,
      displayMaxHigh: 0,
      alternateWidth: 0,
      alternateHeight: 0,
      alternateDepth: 0,
      alternateNumberWide: 0,
      alternateNumberHigh: 0,
      alternateNumberDeep: 0,
      alternateTotalNumber: 0,
      alternateMaxHigh: 0,
      looseWidth: 0,
      looseHeight: 0,
      looseDepth: 0,
      looseNumberWide: 0,
      looseNumberHigh: 0,
      looseNumberDeep: 0,
      looseTotalNumber: 0,
      looseMaxHigh: 0,
      merchxMin: -1,
      merchxMax: -1,
      merchxUprights: -1,
      merchxCaps: -1,
      merchxPlacement: 0,
      merchxNumber: 0,
      merchxSize: 0,
      merchxDirection: -1,
      merchxSqueeze: getPsaSqueezeFlag(item.squeezeWidth),
      merchyMin: -1,
      merchyMax: -1,
      merchyUprights: -1,
      merchyCaps: -1,
      merchyPlacement: 0,
      merchyNumber: 0,
      merchySize: 0,
      merchyDirection: -1,
      merchySqueeze: getPsaSqueezeFlag(item.squeezeHeight),
      merchzMin: -1,
      merchzMax: -1,
      merchzUprights: -1,
      merchzCaps: -1,
      merchzPlacement: 0,
      merchzNumber: 0,
      merchzSize: 0,
      merchzDirection: -1,
      merchzSqueeze: getPsaSqueezeFlag(item.squeezeDepth),
      numberOfPositions: 0,
      desc1: "",
      desc2: "",
      desc3: "",
      desc4: `${item.price}`,
      desc5: "",
      desc6: "",
      desc7: "",
      desc8: "",
      desc9: "",
      desc10: "",
      desc11: "",
      desc12: "",
      desc13: "",
      desc14: "",
      desc15: "",
      desc16: "",
      desc17: "",
      desc18: "",
      desc19: "",
      desc20: "",
      desc21: "",
      desc22: "",
      desc23: "",
      desc24: "",
      desc25: "",
      desc26: "",
      desc27: "",
      desc28: "",
      desc29: "",
      desc30: "",
      desc31: "",
      desc32: "",
      desc33: "",
      desc34: "",
      desc35: "",
      desc36: "",
      desc37: "",
      desc38: "",
      desc39: "",
      desc40: "",
      desc41: "",
      desc42: "",
      desc43: "",
      desc44: "",
      desc45: "",
      desc46: "",
      desc47: "",
      desc48: "",
      desc49: "",
      desc50: "",
      value1: 0,
      value2: 0,
      value3: 0,
      value4: 0,
      value5: 0,
      value6: 0,
      value7: item.quantity,
      value8: 0,
      value9: 0,
      value10: 0,
      value11: 0,
      value12: 0,
      value13: 0,
      value14: 0,
      value15: 0,
      value16: 0,
      value17: 0,
      value18: 0,
      value19: 0,
      value20: 0,
      value21: 0,
      value22: 0,
      value23: 0,
      value24: 0,
      value25: 0,
      value26: 0,
      value27: item.price,
      value28: item.price,
      value29: 0,
      value30: 0,
      value31: 0,
      value32: 0,
      value33: item.price,
      value34: 0,
      value35: 0,
      value36: 0,
      value37: 0,
      value38: 0,
      value39: 0,
      value40: 0,
      value41: 0,
      value42: 0,
      value43: 0,
      value44: 0,
      value45: 0,
      value46: 0,
      value47: 0,
      value48: 0,
      value49: 0,
      value50: 0,
      flag1: 1,
      flag2: 1,
      flag3: 1,
      flag4: 1,
      flag5: 1,
      flag6: 1,
      flag7: 0,
      flag8: "",
      flag9: item.brand,
      flag10: item.subCategory,
      minimumSqueezeFactorX: item.squeezeWidthPct,
      minimumSqueezeFactorY: item.squeezeHeightPct,
      minimumSqueezeFactorZ: item.squeezeDepthPct,
      maximumSqueezeFactorX: 1.0,
      maximumSqueezeFactorY: 1.0,
      maximumSqueezeFactorZ: 1.0,
      fillPattern: 0,
      modelFilename: "",
      brand: item.brand,
      subcategory: item.subCategory,
      weight: 0,
      planogramAlias: 0,
      changed: 0,
      frontOverhang: -1,
      fingerSpaceX: 0,
      dbkey1: -1,
      dbkey2: -1,
      dbkey3: -1,
      dbkey4: -1,
      dbkey5: -1,
      dbkey6: -1,
      dbkey7: -1,
      dbkey8: -1,
      dbkey9: -1,
      dbkey10: -1,
      status: 0,
      dateCreated: 0,
      dateModified: 0,
      datePending: 0,
      dateEffective: 0,
      dateFinished: 0,
      date1: 0,
      date2: 0,
      date3: 0,
      createdBy: "",
      modifiedBy: "",
      transparency: 0,
      peakSafetyFactor: 0,
      backroomStock: 0,
      deliverySchedule: -1,
      partid: 2,
      authorityLevel: "",
      bitmapIdOverrideUnit: 0,
      modelFilenameLookup: -1,
      defaultMerchStyle: 0,
      automaticModel: 0,
      dbguid: item.uniqueId,
      source: 2,
      technicalkey: "",
      squeezeExpandUnitsOnly: 0,
    };

    return psaProduct;
  };

  private convertPlanogramToPsaPlanogram = (planogram: Planogram, planogramName?: string) => {
    const psaPlanogram = {
      object: "Planogram",
      // Store resolution on import regexes the trailing digits out of this
      // name (bare_store_code) — a nameless export matches no store and its
      // quantities bake to zero, so stamp the store code whenever we have one.
      name: planogramName || "EXPORT",
      key: "",
      width: planogram.getWidth() / 100,
      height: planogram.getHeight() / 100,
      depth: planogram.bays[0].depth / 100,
      color: 16777215,
      backDepth: 1.0,
      drawBack: 1.0,
      baseWidth: planogram.getWidth() / 100,
      baseHeight: planogram.bays[0].baseHeight / 100,
      baseDepth: planogram.bays[0].depth / 100,
      drawBase: 1,
      baseColor: 12632256,
      drawNotches: 1,
      notchOffset: 0,
      notchSpacing: planogram.bays[0].notchSpacing / 100,
      doubleNotches: 0,
      notchColor: 0,
      notchMarks: 1,
      drawPegs: 0,
      drawPegholes: 0,
      trafficFlow: 1,
      autoCreated: 0,
      shapeId: "",
      bitmapId: "",

      merchXMin: -1,
      merchXMax: -1,
      merchXUprights: -1,
      merchXCaps: -1,
      merchXPlacementLeftRight: -1,
      merchXNumbersOfFacingsWide: -1,
      merchXWidthOfFacings: -1,
      merchXDirection: -1,
      merchXSqueeze: -1,

      merchYMin: -1,
      merchYMax: -1,
      merchYUprights: -1,
      merchYCaps: -1,
      merchYPlacementUpDown: -1,
      merchYNumbersOfUnitsHigh: -1,
      merchYHeightOfUnits: -1,
      merchYDirection: -1,
      merchYSqueeze: -1,

      merchZMin: -1,
      merchZMax: -1,
      merchZUprights: -1,
      merchZCaps: -1,
      merchZPlacementBackFront: -1,
      merchZNumbersOfUnitsDeep: -1,
      merchZDepthOfUnits: -1,
      merchZDirection: -1,
      merchZSqueeze: -1,

      combinedPerformanceIndex: 0,
      numberOfStores: 1,
      notchWidth: 0,

      desc1: "",
      desc2: "",
      desc3: "",
      desc4: "",
      desc5: "",
      desc6: "",
      desc7: "",
      desc8: "",
      desc9: "",
      desc10: "",
      desc11: "",
      desc12: "",
      desc13: "",
      desc14: "",
      desc15: "",
      desc16: "",
      desc17: "",
      desc18: "",
      desc19: "",
      desc20: "",
      desc21: "",
      desc22: "",
      desc23: "",
      desc24: "",
      desc25: "",
      desc26: "",
      desc27: "",
      desc28: "",
      desc29: "",
      desc30: "",
      desc31: "",
      desc32: "",
      desc33: "",
      desc34: "",
      desc35: "",
      desc36: "",
      desc37: "",
      desc38: "",
      desc39: "",
      desc40: "",
      desc41: "",
      desc42: "",
      desc43: "",
      desc44: "",
      desc45: "",
      desc46: "",
      desc47: "",
      desc48: "",
      desc49: "",
      desc50: "",

      value1: 0.0,
      value2: 0.0,
      value3: 0.0,
      value4: 0.0,
      value5: 0.0,
      value6: 0.0,
      value7: 0.0,
      value8: 0.0,
      value9: 0.0,
      value10: 0.0,
      value11: 0.0,
      value12: 0.0,
      value13: 0.0,
      value14: 0.0,
      value15: 0.0,
      value16: 0.0,
      value17: 0.0,
      value18: 0.0,
      value19: 0.0,
      value20: 0.0,
      value21: 0.0,
      value22: 0.0,
      value23: 0.0,
      value24: 0.0,
      value25: 0.0,
      value26: 0.0,
      value27: 0.0,
      value28: 0.0,
      value29: 0.0,
      value30: 0.0,
      value31: 0.0,
      value32: 0.0,
      value33: 0.0,
      value34: 0.0,
      value35: 0.0,
      value36: 0.0,
      value37: 0.0,
      value38: 0.0,
      value39: 0.0,
      value40: 0.0,
      value41: 0.0,
      value42: 0.0,
      value43: 0.0,
      value44: 0.0,
      value45: 0.0,
      value46: 0.0,
      value47: 0.0,
      value48: 0.0,
      value49: 0.0,
      value50: 0.0,

      flag1: 1,
      flag2: 1,
      flag3: 1,
      flag4: 1,
      flag5: 1,
      flag6: 1,
      flag7: 0,
      flag8: 0,
      flag9: 0,
      flag10: 0,

      fillPattern: 0,
      segmentsToPrint: "",
      fileName: "export.PSA",
      changed: 0,
      layoutFileName: "",
      notes: "",

      dbKey1: -1,
      dbKey2: -1,
      dbKey3: -1,
      dbKey4: -1,
      dbKey5: -1,
      dbKey6: -1,
      dbKey7: -1,
      dbKey8: -1,
      dbKey9: -1,
      dbKey10: -1,

      sourceFileType: 0,
      status1: "",
      status2: "",
      status3: "",
      dateCreated: 0,
      dateModified: 0,
      datePending: 0,
      dateEffective: 0,
      dateFinished: 0,
      date1: 0,
      date2: 0,
      date3: 0,
      createdBy: "",
      modifiedBy: "",
      floorBitmapId: "",
      doorTransparency: 0.5,
      floorTileWidth: 12,
      floorTileDepth: 12,

      inventoryModelManual: 0,
      inventoryModelCaseMultiple: 1,
      inventoryModelDaysSupply: 1,
      inventoryModelPeak: 0,
      inventoryModelMinUnits: 0,
      inventoryModelMaxUnits: 0,

      caseMultiple: 1.0,
      daysSupply: 2,
      demandCycleLength: 1,
      peakSafetyFactor: 1,
      backroomStock: 0,

      demand1: 0,
      demand2: 0,
      demand3: 0,
      demand4: 0,
      demand5: 0,
      demand6: 0,
      demand7: 0,

      deliverySchedule: "",
      id: "",
      department: "",
      partId: "",
      gln: "",
      planogramGuid: uuid(),
      dbGuid: "",
      abbrevName: "",
      category: "",
      subcategory: "",
      source: 0,
      allocationGroup: "",
      allocationSequence: 0,
      allocationTargetMin: 0,
      allocationTargetMax: 0,
      canSegment: 1,
      canSplit: -1,

      pgStatus: -1,
      pgScorePercent: 0,
      pgScoreNote: "",
      pgWarningsCount: 0,
      pgErrorsCount: 0,
      pgActionList: "",
      pgMaxStageReduce: 0,
      pgMaxStageFillOut: 0,
      pgType: 0,
      modelFilename: "",

      dbFamilyKey: -1,
      dbReplaceKey: -1,
      dbVersionKey: -1,
      dbParentPgAuxTemplateKey: -1,
      dbParentPgSourceKey: -1,
      dbParentPgTemplateKey: -1,
      dbPgTimeDone: 0,

      pgServerName: "",
      prStatus: -1,
      movementPeriod: 0,
      allocationSectionSplits: 1,
      allocationPriority: 0,

      checksum1: 0,
      checksum2: 0,
      checksum3: 0,
      checksum4: 0,
      checksum5: 0,

      useAsSubplanogram: 0,
      allocationMerge: "",
      isMerged: 0,
    };

    return psaPlanogram;
  };

  private convertPlanogramBayToPsaSegment = (bay: PlanogramBay) => {
    // Field layout is positional and pinned to ProSpace-authored donor files:
    // the segment's X offset sits at field index 9 (after the width/height/
    // depth pairs), not next to the name. With X anywhere else every bay
    // parses at x=0 and the whole plan renders as one overlapping stack.
    const psaSegment = {
      object: "Segment",
      name: `BAY ${bay.getNo()}`,
      key: `BAY ${bay.getNo()}`,
      pad2: 0,
      width: bay.width / 100,
      pad4: 0,
      height: bay.height / 100,
      pad6: 0,
      depth: bay.depth / 100,
      pad8: 0,
      x: bay.getXLeft() / 100,
      y: 0,
      z: 0,
      doorDirection: 0,

      desc1: "",
      desc2: 8,
      desc3: bay.getNo(),
      desc4: 2,
      desc5: 2,
      desc6: 0,
      desc7: -1,
      desc8: 0,
      desc9: 0,
      desc10: 0,

      value1: 0.0,
      value2: 0.0,
      value3: 0.0,
      value4: 0.0,
      value5: 0.0,
      value6: 0.0,
      value7: 0.0,
      value8: 0.0,
      value9: 0.0,
      value10: 0.0,

      flag1: 0,
      flag2: 0,
      flag3: 0,
      flag4: 0,
      flag5: 0,
      flag6: 0,
      flag7: 0,
      flag8: 0,
      flag9: 0,
      flag10: 0,

      frameWidth: 0.0,
      frameHeight: 0.0,
      changed: 0,
      frameColor: -1,
      frameFillPattern: 0,

      partId: "",
      gln: "",

      canSeparate: 1,
    };

    return psaSegment;
  };

  private convertPlanogramShelfToPsaFixture = (shelf: PlanogramShelf) => {
    const fixtureType = 0;

    const psaFixture = {
      object: "Fixture",
      type: fixtureType,
      name: `Bay ${shelf.bay.getNo()}/${shelf.getNo()}`,
      key: "",
      x: shelf.getXLeft() / 100,
      width: (shelf.getXRight() - shelf.getXLeft()) / 100,
      y: shelf.getYBottom() / 100,
      height: shelf.thickness / 100,
      z: 0,
      depth: shelf.depth / 100,
      slope: 0,
      angle: 0,
      roll: 0,
      color: 8421504,
      assembly: "",
      xSpacing: 0,
      ySpacing: 0,
      xStart: 0,
      yStart: 0,
      wallWidth: 0,
      wallHeight: 0,
      wallDepth: 0,
      curve: 0,
      // Merchandisable height: the clear space from this shelf's top surface
      // to the bottom of the shelf above (bay top for the topmost shelf).
      // Space Planning stacks units by this value, so a constant over-stacks
      // through the shelf above wherever the real gap is smaller.
      spaceMerchandisableSpace:
        Math.max(0, shelf.getYTop() - shelf.getYBottom() - shelf.thickness) / 100,
      spaceDetectOtherFixtures: 1,
      spaceDetectPositionsOrOtherFixtures: 1,
      canObstruct: 1,
      leftOverhang: 0,
      rightOverhang: 0,
      lowerOverhang: 0,
      upperOverhang: 0,
      backOverhang: 0,
      frontOverhang: 0,
      defaultMerchStyle: 0,
      dividerWidth: 0,
      dividerHeight: 0,
      dividerDepth: 0,
      // The pog records which neighbors this shelf actually merges with;
      // exporting anything broader invents merges on re-import (and anything
      // narrower breaks placements that span bays).
      canCombine:
        shelf.mergedLeft && shelf.mergedRight
          ? FixtureCanCombine.BOTH
          : shelf.mergedLeft
            ? FixtureCanCombine.LEFT
            : shelf.mergedRight
              ? FixtureCanCombine.RIGHT
              : FixtureCanCombine.NONE,
      grilleHeight: 0,
      notchOffset: 0,
      xSpacing2: 0,
      xStart2: 0,
      pegDrop: 0,
      pegGapX: 0,
      pegGapY: 0,
      primaryFixtureLabelFormatName: "",
      secondaryFixtureLabelFormatName: "",
      shapeId: "",
      bitmapId: "",
      merchxMin: -1,
      merchxMax: -1,
      merchxUprights: -1,
      merchxCaps: -1,
      merchxPlacement: 0,
      merchxNumber: 0,
      merchxSize: 0,
      merchxDirection: -1,
      merchxSqueeze: -1,
      merchyMin: -1,
      merchyMax: 0,
      merchyUprights: -1,
      merchyCaps: -1,
      merchyPlacement: 2,
      merchyNumber: 3,
      merchySize: 1,
      merchyDirection: 0,
      merchySqueeze: -1,
      merchzMin: -1,
      merchzMax: -1,
      merchzUprights: -1,
      merchzCaps: -1,
      merchzPlacement: 2,
      merchzNumber: 3,
      merchzSize: 1,
      merchzDirection: 1,
      merchzSqueeze: -1,
      desc1: shelf.uniqueId,
      desc2: fixtureType,
      desc3: -1,
      desc4: 0,
      desc5: 0,
      desc6: 0,
      desc7: 0,
      desc8: 1,
      desc9: 0,
      desc10: 0,
      desc11: "",
      desc12: "",
      desc13: 0,
      desc14: "",
      desc15: shelf.getYTop() / 100,
      desc16: shelf.getNotchNo(),
      desc17: 0,
      desc18: 0,
      desc19: 0,
      desc20: 0,
      desc21: "",
      desc22: "",
      desc23: "",
      desc24: "",
      desc25: "",
      desc26: "",
      desc27: "",
      desc28: "",
      desc29: "",
      desc30: "",
      value1: 0,
      value2: 0,
      value3: 0,
      value4: 0,
      value5: 0,
      value6: 0,
      value7: 0,
      value8: 0,
      value9: 0,
      value10: 0,
      value11: 0,
      value12: 0,
      value13: 0,
      value14: 0,
      value15: 0,
      value16: 0,
      value17: 0,
      value18: 0,
      value19: 0,
      value20: 0,
      value21: 0,
      value22: 0,
      value23: 0,
      value24: 0,
      value25: 0,
      value26: 0,
      value27: 0,
      value28: 0,
      value29: 0,
      value30: 0,
      flag1: 0,
      flag2: 0,
      flag3: 0,
      flag4: 0,
      flag5: 0,
      flag6: 0,
      flag7: 0,
      flag8: 0,
      flag9: 0,
      flag10: 0,
      locationId: 1,
      fillPattern: 0,
      modelFilename: "",
      weightCapacity: 0,
      changed: 0,
      dividerAtStart: 0,
      dividerAtEnd: 0,
      dividersBetweenFacings: 0,
      transparency: 0,
      hideIfPrinting: 0,
      productAssociation: "",
      partid: "",
      hideViewDimensions: 1,
      gln: "",
      canAttach: 0,
      attachedToFixture: "<None>",
      isAttached: 0,
      rankX: -1,
      rankY: -1,
      rankZ: -1,
      fixtureGuid: shelf.uniqueId,
    };

    return psaFixture;
  };

  private convertPlanogramItemToPsaPosition = (
    item: PlanogramItem,
    productId: PlanogramItem["productCode"] = item.productCode,
  ) => {
    const itemCoordinates = item.getCoordinates();
    const psaPosition = {
      object: "Position",
      upc: item.upc,
      id: productId,
      key: "",
      x: itemCoordinates.xLeft / 100,
      width: (item.getWidth() / 100) * item.getHorizontalFacings(),
      y: itemCoordinates.yBottom / 100,
      height: (item.getHeight() / 100) * item.getVerticalFacings(),
      z: 0,
      depth: (item.getDepth() / 100) * item.getDepthFacings(),
      slope: 0.0,
      angle: 0.0,
      roll: 0.0,
      merchandisingStyle: getMerchandisingStyleCode(item.merchandisingStyle),
      horizontalFacings: item.getHorizontalFacings(),
      verticalFacings: item.getVerticalFacings(),
      depthFacings: item.getDepthFacings(),

      xCapNum: item.xCapNum,
      xCapNested: 0,
      xCapReversed: 0,
      merchXCappingOrientation: getOrientationCode(item.xCapOrientation),

      yCapNum: item.yCapNum,
      yCapNested: 0,
      yCapReversed: 0,
      merchYCappingOrientation: getOrientationCode(item.yCapOrientation),

      zCapNum: item.zCapNum,
      zCapNested: 0,
      zCapReversed: 0,
      merchZCappingOrientation: getOrientationCode(item.zCapOrientation),

      orientation: getOrientationCode(item.orientation),
      jumbleWidth: 0,
      jumbleHeight: 0,
      jumbleDepth: 0,

      merchStyleWidth: item.getWidth() / 100,
      merchStyleHeight: item.getHeight() / 100,
      merchStyleDepth: item.getDepth() / 100,

      fullWidth: item.unitWidth / 100,
      fullHeight: item.unitHeight / 100,
      fullDepth: item.unitDepth / 100,

      xSubUnits: 1,
      ySubUnits: 1,
      zSubUnits: 1,

      pegId: "",
      manualUnits: 1,
      rankX: 1,
      rankY: 1,
      rankZ: 1,
      pegSpan: 0,
      alwaysFloat: 0,
      primaryPositionLabelFormatName: "",
      secondaryPositionLabelFormatName: "",

      merchXMin: -1,
      merchXMax: -1,
      merchXUprights: -1,
      merchXCaps: -1,
      merchXPlacement: 0,
      merchXNumbersOfFacingsWide: -1,
      merchXWidthOfFacings: getPsaSqueezeSizeMode(item.squeezeWidth),
      merchXDirection: -1,
      merchXSqueeze: getPsaSqueezeFlag(item.squeezeWidth),

      merchYMin: -1,
      merchYMax: -1,
      merchYUprights: -1,
      merchYCaps: -1,
      merchYPlacement: 0,
      merchYNumbersOfUnitsHigh: -1,
      merchYHeightOfUnits: getPsaSqueezeSizeMode(item.squeezeHeight),
      merchYDirection: -1,
      merchYSqueeze: getPsaSqueezeFlag(item.squeezeHeight),

      merchZMin: -1,
      merchZMax: -1,
      merchZUprights: -1,
      merchZCaps: -1,
      merchZPlacement: 0,
      merchZNumbersOfUnitsDeep: -1,
      merchZDepthOfUnits: getPsaSqueezeSizeMode(item.squeezeDepth),
      merchZDirection: -1,
      merchZSqueeze: getPsaSqueezeFlag(item.squeezeDepth),

      desc1: "",
      desc2: "",
      desc3: item.name,
      desc4: "",
      desc5: "",
      desc6: "",
      desc7: "",
      desc8: "",
      desc9: "",
      desc10: "",
      desc11: "",
      desc12: "",
      desc13: "",
      desc14: "",
      desc15: "",
      desc16: "",
      desc17: "",
      desc18: "",
      desc19: "",
      desc20: "",
      desc21: "",
      desc22: "",
      desc23: "",
      desc24: "",
      desc25: "",
      desc26: "",
      desc27: "",
      desc28: "",
      desc29: "",
      desc30: "",

      value1: 0.0,
      value2: 0.0,
      value3: 0.0,
      value4: 0.0,
      value5: 0.0,
      value6: 0.0,
      value7: 0.0,
      value8: 0.0,
      value9: 0.0,
      value10: 0.0,
      value11: 0.0,
      value12: 0.0,
      value13: 0.0,
      value14: 0.0,
      value15: 0.0,
      value16: 0.0,
      value17: 0.0,
      value18: 0.0,
      value19: 0.0,
      value20: 0.0,
      value21: 0.0,
      value22: 0.0,
      value23: 0.0,
      value24: 0.0,
      value25: 0.0,
      value26: 0.0,
      value27: 0.0,
      value28: 0.0,
      value29: 0.0,
      value30: 0.0,

      flag1: 0,
      flag2: 0,
      flag3: 0,
      flag4: 0,
      flag5: 0,
      flag6: 0,
      flag7: 0,
      flag8: 0,
      flag9: 0,
      flag10: 0,

      useTargetSpaceX: 0,
      useTargetSpaceY: 0,
      useTargetSpaceZ: 0,
      targetSpaceX: 0,
      targetSpaceY: 0,
      targetSpaceZ: 0,

      locationId: "",
      changed: 0,
      replenishmentMin: 0,
      replenishmentMax: 0,
      shapeId: "",
      bitmapIdOverride: "",
      hideIfPrinting: 0,
      partId: "",
      bitmapIdOverrideUnit: 0,
      automaticModel: -1,
      xCapWithUnits: 0,
      yCapWithUnits: 0,
      zCapWithUnits: 0,
    };

    return psaPosition;
  };
}

const psaAdapter = new PsaAdapter();
export default psaAdapter;
